import type { ReactElement } from "react";
import { useCallback, useMemo } from "react";

import type { IFormSelectProps } from "app/pages/sales-intelligence/common-components/form-fields";
import { FormSelect } from "app/pages/sales-intelligence/common-components/form-fields";
import { FormCheckboxDropdownItem } from "app/pages/sales-intelligence/common-components/form-fields/components/FormCheckboxDropdownItem/FormCheckboxDropdownItem";
import type { FormSelectValue } from "app/pages/sales-intelligence/common-components/form-fields/components/FormSelect/types";

interface IMultiSelectAutocompleteProps<V = FormSelectValue>
    extends Omit<IFormSelectProps<string>, "value" | "options" | "onChange"> {
    value: readonly FormSelectValue[];
    options: readonly V[];
    onChange(value: FormSelectValue[]): void;

    getOptionId(value: V): FormSelectValue;
    getOptionTitle(value: V): string;
    getOptionSubTitle(value: V): string;
    renderOptionTag?(option: V): ReactElement | null;
}

export const MultiSelectAutocomplete = <V = FormSelectValue,>({
    value,
    options,
    onChange,
    formatValue,
    getOptionId,
    getOptionTitle,
    getOptionSubTitle,
    renderOptionTag,
    ...props
}: IMultiSelectAutocompleteProps<V>): ReactElement => {
    const baseOptions = useMemo(() => options.map(getOptionId), [options]);
    const optionsById: Record<string, V> = useMemo(
        () =>
            options.reduce(
                (acum, option) => ({
                    ...acum,
                    [getOptionId(option)]: option,
                }),
                {},
            ),
        [options, getOptionId],
    );

    const onItemSelect = (optionId: string): void => {
        if (value.includes(optionId)) {
            onChange(value.filter((option) => option !== optionId));

            return;
        }

        onChange([...value, optionId]);
    };

    const renderTag = useCallback(
        (optionId: string): ReactElement | null => {
            if (!renderOptionTag) {
                return null;
            }

            const option: V = optionsById[optionId];

            return renderOptionTag(option);
        },
        [renderOptionTag, optionsById],
    );

    const renderDropdownItem = (optionId: string) => {
        const option: V = optionsById[optionId];

        return (
            <FormCheckboxDropdownItem
                key={optionId}
                id={optionId}
                title={getOptionTitle(option)}
                subtitle={getOptionSubTitle(option)}
                renderTag={renderTag}
            />
        );
    };

    const handleSearchFilter = (query: string, { props }: ReactElement<{ id: string }>): boolean => {
        const optionId = props.id;
        const option: V = optionsById[optionId];

        return new RegExp(query, "gi").test(`${getOptionTitle(option)} ${getOptionSubTitle(option)}`);
    };

    return (
        <FormSelect<FormSelectValue>
            renderItem={renderDropdownItem}
            searchFilter={handleSearchFilter}
            {...props}
            dropdownPopupHeight={350}
            value={value}
            options={baseOptions}
            closeOnItemClick={false}
            onChange={onItemSelect}
        />
    );
};
